import { Link } from 'react-router-dom';
import { useState, useContext, useEffect } from 'react';
import { AppContext } from '../util/AppContext';
import { getConfigValue } from '../util/getConfigValue';
import CreateSalesAidStepProgressBar, { SalesAidStepIds } from '../components/CreateSalesAidStepProgressBar.js';
import SelectTargetComponent from '../components/SelectTargetComponent.js';
import { useRoutingValidation } from '../util/useRoutingValidation.js';
import { useBreadcrumbs } from '../util/BreadcrumbContext.js';
import { getPrescribersByTerritory, getPrescriberById } from '../API/prescriber.js';
import { getGroupPracticesByTerritory, getGroupPracticeById } from '../API/groupPractice.js';
import { getGeographyAggregatesByTerritory, getGeographyAggregateById } from '../API/geographyAggregate.js';
import { filterByValue } from '../util/filterByValue.js';
import { hcpType, hcpTab, quickSheetTypeMessageId } from '../util/constants.js';
import { useErrorBoundary } from 'react-error-boundary';
import { getQuickSheets } from "../API/quickSheets";

const HCPPage = () => {
	const [appState, setAppState] = useContext(AppContext);
	const [searchInput, setSearchInput] = useState('');
	const [activeId, setActiveId] = useState(null);
	const [showResults, setShowResults] = useState(true);

	const topButtonsEnabled = getConfigValue(appState.config.Target.TopButtons?.Enabled, appState)?.Value === 'true';
	const useStickyNavigation = getConfigValue(appState.config.CreateSalesAid?.Features?.StickyNavigation?.Enabled, appState)?.Value === 'true';
	const { setBreadcrumbs } = useBreadcrumbs();
	useEffect(() => {
		setBreadcrumbs([{ label: getConfigValue(appState.config?.Focus?.Header, appState)?.Value, path: '/FocusPage' }]);
	}, [setBreadcrumbs]);

	useRoutingValidation();

	const { showBoundary } = useErrorBoundary();

	useEffect(() => {
		if (appState.prescriber) {
			setActiveId(appState.prescriber.id);
		}
	}, [appState.prescriber, setActiveId]);

	function handleChange(event) {
		setSearchInput(event.target.value);
	}

	const getPrescribers = async () => {
		return !appState?.market?.id || !appState?.territory?.id
			? []
			: await getPrescribersByTerritory(appState.market.id, appState.territory.id);            
	};

	const getGroupPractices = async () => {
		return !appState?.market?.id || !appState?.territory?.id
			? []
			: await getGroupPracticesByTerritory(appState.market.id, appState.territory.id);
	};

	const getGeographyAggregates = async () => {
		return !appState?.market?.id || !appState?.territory?.id
			? []
			: await getGeographyAggregatesByTerritory(appState.market.id, appState.territory.id);
	};

	const filter = async function (data, searchInput, hcpFavoritesOnly) {
		if (hcpFavoritesOnly) {
			data = data.filter((row) => row.data.favorite);
		}
		if (searchInput.length > 0) {
			data = filterByValue(data, searchInput.trim(), [], true);
		}
		return data;
	};

	const handleActive = (data, activeTab) => {
		const enabledProducts = appState.sellSheetProductDetails?.map((p) => p.clientProductId);

		if (activeTab === hcpTab.Prescriber) {
			getPrescriberById(data.id, appState?.market?.id, appState?.territory?.id)
				.then(async (data) => {
					await enrichQuickSheetsMessageType(data);
					const prescriber = data;
					const products = prescriber?.products.filter((product) => enabledProducts.includes(product.productId)).map((product) => product) || null;

					setAppState({
						...appState,
						prescriber: prescriber,
						prescriberType: hcpType.Prescriber,
						products: products,
					});
				})
				.catch((error) => {
					showBoundary(error);
				});
			setActiveId(data.id);
		} else if (activeTab === hcpTab.GroupPractice) {
			getGroupPracticeById(data.id, appState?.market?.id, appState?.territory?.id)
				.then(async (data) => {
					await enrichQuickSheetsMessageType(data);
					const prescriber = data;
					const products = prescriber?.products.filter((product) => enabledProducts.includes(product.productId)).map((product) => product) || null;
					setAppState({
						...appState,
						prescriber: prescriber,
						prescriberType: hcpType.GroupPractice,
						products: products,
					});
				})
				.catch((error) => {
					showBoundary(error);
				});
			setActiveId(data.id);
		} else if (activeTab === hcpTab.GeographyAggregate) {
			getGeographyAggregateById(data.id, appState?.market?.id, appState?.territory?.id)
				.then(async (data) => {
					await enrichQuickSheetsMessageType(data);
					const prescriber = data;
					const products = prescriber?.products.filter((product) => enabledProducts.includes(product.productId)).map((product) => product) || null;
					setAppState({
						...appState,
						prescriber: prescriber,
						prescriberType: hcpType.GeographyAggregate,
						products: products,
					});
				})
				.catch((error) => {
					showBoundary(error);
				});
			setActiveId(data.id);
		}
	};

	async function enrichQuickSheetsMessageType(data) {
		if(getConfigValue(appState.config?.QuickSheets?.MessageType?.Enabled, appState)?.Value === 'true') {
			const enrichmentPromises = data.products?.map(async (product) => {
				// determine if the brand and territory has any quicksheets or if there are national quicksheets
				const hasQuickSheets = await getQuickSheets(product.productId, true, false, appState?.territory?.id, data.id)
				.then((data) => {
					return data.length > 0;
				})
				.catch((error) => {
					showBoundary(error);
					return false;
				});

				if (hasQuickSheets){
					// add in the quicksheets message type
					product.messages?.push({
						category: getConfigValue(appState.config?.NavBar?.Menu?.DownloadQuickSheet?.Label, appState)?.Value,
						categoryDisplayOrder: 99,
						isContainerMessage: false,
						message: getConfigValue(appState.config?.QuickSheets?.MessageType?.Description, appState)?.Value,
						messagecode: 'QUICKSHEETS',
						messageTypeId: quickSheetTypeMessageId
					});
				}
			}) || [];

			await Promise.all(enrichmentPromises);
		}
	}	

	return (
		<div>
			<CreateSalesAidStepProgressBar currentStepId={SalesAidStepIds.SELECTTARGET} focusType={appState?.focus} />

			<div className='container-xl mt-5'>
				<div className='row justify-content-center mb-6'>
					<div className='col-12 col-md-12'>
						<h1 className='type-20 color-grey-d1'>{getConfigValue(appState.config?.Target?.SelectTarget, appState)?.Value}</h1>
						{topButtonsEnabled && (
							<div className='row justify-content-end mb-5'>
								<div className='col-10 col-sm-8 col-md-6 offset-md-4 col-lg-5 offset-lg-5 col-xxl-5 offset-xxl-6 justify-content-end ps-5'>
									<div className='d-flex flex-column flex-md-row-reverse'>
										{(!activeId || !showResults) && (
											<Link to='#' className='btn btn-primary disabled btn-lg pe-7 mb-3 mb-md-0 ms-md-3 flex-fill w-100'>
												{getConfigValue(appState.config?.Common?.Continue, appState)?.Value}
											</Link>
										)}
										{activeId && showResults && (
											<Link to='/MessageSelectionPage' className='btn btn-primary btn-lg pe-7 mb-3 mb-md-0 ms-md-3 flex-fill w-100'>
												{getConfigValue(appState.config?.Common?.Continue, appState)?.Value}
											</Link>
										)}
										<Link
											to={appState?.bypassTerritorySelection ? '/FocusPage' : '/TerritoryPage'}
											className='btn btn-secondary btn-lg pe-7'
										>
											{getConfigValue(appState.config?.Common?.Back, appState)?.Value}
										</Link>
									</div>
								</div>
							</div>
						)}

						<SelectTargetComponent
							filterFunction={filter}
							handleChange={handleChange}
							activeId={activeId}
							setShowResults={setShowResults}
							showResults={showResults}
							getPrescribers={getPrescribers}
							getGroupPractices={getGroupPractices}
							getGeographyAggregates={getGeographyAggregates}
							handleActive={handleActive}
							searchInput={searchInput}
							setSearchInput={setSearchInput}
						/>
					</div>
				</div>
            </div>

			<div className={`${useStickyNavigation ? 'button-bottom-container-sticky-low-z' : ''}`}>
				<div className='container-xl'>
					<div className='row justify-content-center justify-content-lg-between py-3'>
						<div className='col-10 col-sm-8 col-md-6 col-xl-5'>
							<div className='d-flex flex-column flex-lg-row'>
								<div className='flex-fill w-100 ms-lg-3 pe-3'></div>
							</div>
						</div>
						<div className='w-100 d-lg-none'></div>
						<div className='col-12'>
							<div className="d-flex flex-column flex-md-row-reverse" style={{ float: "right" }}>
								{(!activeId || !showResults) && (
									<Link to='#' className='btn btn-primary disabled btn-lg pe-7 flex-fill w-100 ml-1'>
										{getConfigValue(appState.config?.Common?.Continue, appState)?.Value}
									</Link>
								)}
								{activeId && showResults && (
									<Link to='/MessageSelectionPage' className='btn btn-primary btn-lg pe-7 flex-fill w-100 ml-1'>
										{getConfigValue(appState.config?.Common?.Continue, appState)?.Value}
									</Link>
								)}
								<Link to={appState?.bypassTerritorySelection ? '/FocusPage' : '/TerritoryPage'} className='btn btn-secondary btn-lg pe-7 flex-fill w-100'>
									{getConfigValue(appState.config?.Common?.Back, appState)?.Value}
								</Link>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default HCPPage;
